
import { Box, Flex, Grid, Portal, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type React from 'react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { CalendarIcon } from '@/assets/icons';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import Show from '@/components/Molecules/Show';
import { formatDateToString } from '@/utils/dateUtils';
import {
	DAY_NAMES,
	type DateRange,
	getCalendarHeaderText,
	useCalendarDay,
	useDatePicker,
} from './utils';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Get today's date in New York timezone
 */
export const getTodayInNYTimezone = (): Date => {
	return dayjs().tz('America/New_York').startOf('day').toDate();
};

// Extend interface to avoid TypeScript issues
export interface PopoverContentPropsExtended {
	children?: React.ReactNode;
}

export interface FaceliftDatePickerProps {
	topLabel?: string;
	placeholder?: string;
	error?: string;
	required?: boolean;
	onChange?: (date: string) => void;
	onRangeChange?: (range: DateRange) => void;
	value?: string;
	range?: DateRange;
	isRange?: boolean;
	minDate?: Date;
	maxDate?: Date;
	disabled?: boolean;
	defaultToToday?: boolean;
}

/**
 * Calendar Day Button Component
 */
const CalendarDayButton: React.FC<{
	day: number;
	currentMonth: Date;
	selectedDate?: Date;
	selectedRange?: { startDate?: Date; endDate?: Date };
	isRange?: boolean;
	disabled?: boolean;
	minDate?: Date;
	maxDate?: Date;
	onSelect: (day: number) => void;
}> = ({
	day,
	currentMonth,
	selectedDate,
	selectedRange,
	isRange,
	disabled,
	minDate,
	maxDate,
	onSelect,
}) => {
	const { isSelected, isToday, dayDate, isInRange, isRangeStart, isRangeEnd } =
		useCalendarDay(currentMonth, day, selectedDate, selectedRange, isRange);

	// Check if this date should be disabled based on minDate/maxDate
	const isDateDisabled = () => {
		if (disabled) return true;

		if (minDate && dayDate < minDate) return true;
		if (maxDate && dayDate > maxDate) return true;

		return false;
	};

	const dateDisabled = isDateDisabled();

	// Determine styling based on range state and disabled state
	const getButtonStyling = () => {
		if (dateDisabled) {
			return {
				bg: 'transparent',
				backgroundImage: 'none',
				color: 'gray.400',
				borderRadius: { base: 'md', sm: 'lg' },
				opacity: 0.5,
				cursor: 'not-allowed',
				_dark: { color: 'gray.600' },
			};
		}

		if (isRange) {
			if (isRangeStart || isRangeEnd) {
				return {
					bg: 'transparent',
					backgroundImage: 'linear-gradient(90deg, #1C4B74 0%, #117964 100%)',
					color: 'white',
					borderRadius: { base: 'md', sm: 'lg' },
				};
			}
			if (isInRange) {
				return {
					bg: 'blue.50',
					backgroundImage: 'none',
					color: 'var(--chakra-colors-chakra-body-text)',
					borderRadius: '0',
					_dark: { bg: 'gray.600' },
				};
			}
		} else if (isSelected) {
			return {
				bg: 'transparent',
				backgroundImage: 'linear-gradient(90deg, #1C4B74 0%, #117964 100%)',
				color: 'white',
				borderRadius: { base: 'md', sm: 'lg' },
			};
		}

		return {
			bg: 'transparent',
			backgroundImage: 'none',
			color: 'var(--chakra-colors-chakra-body-text)',
			borderRadius: { base: 'md', sm: 'lg' },
		};
	};

	const buttonStyling = getButtonStyling();

	return (
		<FaceliftButton
			variant="ghost"
			size="xs"
			w="full"
			h={{ base: 8, sm: 7 }}
			fontSize={{ base: '2xs', sm: 'xs' }}
			fontWeight={
				isSelected || isRangeStart || isRangeEnd ? 'semibold' : 'medium'
			}
			bg={buttonStyling.bg}
			backgroundImage={buttonStyling.backgroundImage}
			_dark={buttonStyling._dark}
			border={
				isToday && !isSelected && !isInRange && !isRangeStart && !isRangeEnd
					? { base: '1px solid black', _dark: '1px solid white' }
					: 'none'
			}
			borderRadius={buttonStyling.borderRadius}
			style={{
				color:
					buttonStyling.color === 'white'
						? 'white'
						: 'var(--chakra-colors-chakra-body-text)',
			}}
			_hover={
				dateDisabled
					? {}
					: {
							bg:
								isSelected || isRangeStart || isRangeEnd
									? 'transparent'
									: { base: 'gray.100', _dark: 'gray.700' },
							backgroundImage:
								isSelected || isRangeStart || isRangeEnd
									? 'linear-gradient(90deg, #1C4B74 0%, #117964 100%)'
									: 'none',
						}
			}
			onClick={() => !dateDisabled && onSelect(day)}
			disabled={dateDisabled}
		>
			{day}
		</FaceliftButton>
	);
};

/**
 * Calendar Grid Component
 */
const CalendarGrid: React.FC<{
	currentMonth: Date;
	selectedDate?: Date;
	selectedRange?: { startDate?: Date; endDate?: Date };
	isRange?: boolean;
	disabled?: boolean;
	minDate?: Date;
	maxDate?: Date;
	onDateSelect: (day: number) => void;
	calendarDays: (number | null)[];
}> = ({
	currentMonth,
	selectedDate,
	selectedRange,
	isRange,
	disabled,
	minDate,
	maxDate,
	onDateSelect,
	calendarDays,
}) => {
	return (
		<Grid templateColumns="repeat(7, 1fr)" gap={{ base: 0.5, sm: 1 }}>
			{calendarDays.map((day, index) => (
				<Box key={day ? `day-${day}` : `empty-${index}`} textAlign="center">
					<Show when={!!day}>
						<CalendarDayButton
							day={day!}
							currentMonth={currentMonth}
							selectedDate={selectedDate}
							selectedRange={selectedRange}
							isRange={isRange}
							disabled={disabled}
							minDate={minDate}
							maxDate={maxDate}
							onSelect={onDateSelect}
						/>
					</Show>
					<Show when={!day}>
						<Box h={{ base: 8, sm: 7 }} />
					</Show>
				</Box>
			))}
		</Grid>
	);
};

/**
 * Calendar Header Component
 */
const CalendarHeader: React.FC<{
	currentMonth: Date;
	onPrevMonth: () => void;
	onNextMonth: () => void;
}> = ({ currentMonth, onPrevMonth, onNextMonth }) => {
	return (
		<Flex justify="space-between" align="center" mb={{ base: 2, sm: 3 }}>
			<FaceliftButton
				variant="ghost"
				size="xs"
				onClick={onPrevMonth}
				bg="transparent"
				_hover={{
					bg: 'transparent',
					backgroundImage: 'linear-gradient(90deg, #1C4B74 0%, #117964 100%)',
					color: 'white',
				}}
				fontSize={{ base: 'md', sm: 'lg' }}
				minW={{ base: 6, sm: 8 }}
				h={{ base: 6, sm: 8 }}
				style={{
					color: 'var(--chakra-colors-chakra-body-text)',
				}}
			>
				‹
			</FaceliftButton>
			<Text
				fontWeight="semibold"
				fontSize={{ base: 'xs', sm: 'sm' }}
				textAlign="center"
				flex="1"
			>
				{getCalendarHeaderText(currentMonth)}
			</Text>
			<FaceliftButton
				variant="ghost"
				size="xs"
				onClick={onNextMonth}
				bg="transparent"
				_hover={{
					bg: 'transparent',
					backgroundImage: 'linear-gradient(90deg, #1C4B74 0%, #117964 100%)',
					color: 'white',
				}}
				fontSize={{ base: 'md', sm: 'lg' }}
				minW={{ base: 6, sm: 8 }}
				h={{ base: 6, sm: 8 }}
				style={{
					color: 'var(--chakra-colors-chakra-body-text)',
				}}
			>
				›
			</FaceliftButton>
		</Flex>
	);
};

/**
 * Day Names Header Component
 */
const DayNamesHeader: React.FC = () => {
	return (
		<Grid
			templateColumns="repeat(7, 1fr)"
			gap={{ base: 0.5, sm: 1 }}
			mb={{ base: 1, sm: 2 }}
		>
			{DAY_NAMES.map((day) => (
				<Box
					key={day}
					textAlign="center"
					fontSize={{ base: '2xs', sm: 'xs' }}
					fontWeight="medium"
					py={{ base: 0.5, sm: 1 }}
					color={{ base: 'black', _dark: 'white' }}
				>
					{day}
				</Box>
			))}
		</Grid>
	);
};

/**
 * FaceliftDatePicker Component
 */
const FaceliftDatePicker: React.FC<FaceliftDatePickerProps> = ({
	topLabel,
	placeholder,
	error,
	required = false,
	onChange,
	onRangeChange,
	value = '',
	range,
	isRange = false,
	minDate,
	maxDate,
	disabled = false,
	defaultToToday = false,
}) => {
	// Set default placeholder based on mode
	const defaultPlaceholder = isRange ? 'MM/DD/YYYY - MM/DD/YYYY' : 'MM/DD/YYYY';
	const actualPlaceholder = placeholder || defaultPlaceholder;

	// Auto-select today's date if defaultToToday is true and no value is set
	useEffect(() => {
		if (defaultToToday && !isRange && !value && onChange) {
			const todayDate = getTodayInNYTimezone();
			const formattedDate = formatDateToString(todayDate);
			onChange(formattedDate);
		}
	}, [defaultToToday, isRange, value, onChange]);

	const {
		selectedDate,
		selectedRange,
		isCalendarOpen,
		currentMonth,
		calendarDays,
		containerRef,
		handleCalendarClick,
		handleInputChange,
		handleInputBlur,
		handleInputFocus,
		handleInputClick,
		handleDateSelect,
		handlePrevMonth,
		handleNextMonth,
		backdropProps,
		contentProps,
	} = useDatePicker(
		value,
		onChange,
		onRangeChange,
		range,
		isRange,
		disabled,
		minDate,
		maxDate,
	);

	// Display value logic
	const getDisplayValue = () => {
		if (isRange && range) {
			const startDate = range.startDate || '';
			const endDate = range.endDate || '';
			if (startDate && endDate) {
				return `${startDate} - ${endDate}`;
			}
			if (startDate) {
				return startDate;
			}
			return '';
		}
		return value || '';
	};

	const displayValue = getDisplayValue();

	// State for scroll-triggered position updates
	const [scrollTrigger, setScrollTrigger] = useState(0);

	// Calculate calendar position synchronously during render (no flash!)
	const calendarPosition = useMemo(() => {
		if (!isCalendarOpen) return null;
		
		if (!containerRef.current) {
			return { top: '50%', left: '50%', centered: true };
		}

		const rect = containerRef.current.getBoundingClientRect();
		const calendarWidth = isRange ? 370 : 320;
		const calendarHeight = 280;
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const padding = 16;
		const gap = 8;

		const spaceBelow = viewportHeight - rect.bottom;
		const spaceAbove = rect.top;
		const shouldPositionAbove = spaceBelow < calendarHeight && spaceAbove > spaceBelow;

		let top: number;
		if (shouldPositionAbove) {
			top = rect.top - calendarHeight - gap;
			if (top < padding) {
				top = padding;
			}
		} else {
			top = rect.bottom + gap;
			if (top + calendarHeight > viewportHeight - padding) {
				top = viewportHeight - calendarHeight - padding;
			}
		}

		let left = rect.left;
		if (left + calendarWidth > viewportWidth - padding) {
			left = Math.max(padding, rect.right - calendarWidth);
		}
		if (left < padding) {
			left = padding;
		}

		return { top: `${top}px`, left: `${left}px`, centered: false };
	}, [isCalendarOpen, isRange, scrollTrigger]);

	// Update position on scroll by triggering re-calculation
	useEffect(() => {
		if (!isCalendarOpen) return;
		
		const handleScroll = () => {
			setScrollTrigger(prev => prev + 1);
		};
		
		window.addEventListener('scroll', handleScroll, true);
		return () => window.removeEventListener('scroll', handleScroll, true);
	}, [isCalendarOpen]);

	return (
		<Box position="relative" ref={containerRef}>
			{/* Date Input */}
			<FaceliftInput
				topLabel={topLabel}
				required={required}
				error={error}
				placeholder={actualPlaceholder}
				value={displayValue}
				disabled={disabled}
				showIconWithError
				onChange={handleInputChange}
				onBlur={handleInputBlur}
				onFocus={handleInputFocus}
				onClick={handleInputClick}
				endIcon={
					<Box
						cursor={disabled ? 'not-allowed' : 'pointer'}
						onClick={handleCalendarClick}
					>
						<CalendarIcon width={20} height={19} />
					</Box>
				}
				aria-label={
					topLabel ||
					(isRange ? 'Date range picker input' : 'Date picker input')
				}
			/>

			{/* Calendar Dropdown */}
			<Show when={isCalendarOpen}>
				{/* Backdrop Portal */}
				<Portal>
					<Box data-facelift-date-picker-portal="backdrop" {...backdropProps} />
				</Portal>

				{/* Calendar Content */}
				{/* Calendar Content - Wrapped in Portal to escape parent constraints */}
				<Portal>
					<Box
						data-facelift-date-picker-portal="content"
						{...contentProps}
						position="fixed"
						style={{
							top: calendarPosition?.top || '50%',
							left: calendarPosition?.left || '50%',
							transform: calendarPosition?.centered ? 'translate(-50%, -50%)' : 'none',
							zIndex: 1501,
							pointerEvents: 'auto',
						}}
						zIndex={1501}
						bg={{ base: 'white', _dark: 'gray.800' }}
						boxShadow="xl"
						borderRadius={{ base: 'md', sm: 'lg' }}
						maxW={{ base: '75vw', sm: 'none' }}
						w={{ base: 'max-content', sm: 'auto' }}
						minW={{ base: 'auto', sm: isRange ? '350px' : '300px' }}
						p={{ base: 2, sm: 4 }}
					>
						{/* Calendar Header */}
						<CalendarHeader
							currentMonth={currentMonth}
							onPrevMonth={handlePrevMonth}
							onNextMonth={handleNextMonth}
						/>

						{/* Day Names Header */}
						<DayNamesHeader />

						{/* Calendar Days */}
						<CalendarGrid
							currentMonth={currentMonth}
							selectedDate={selectedDate}
							selectedRange={selectedRange}
							isRange={isRange}
							disabled={disabled}
							minDate={minDate}
							maxDate={maxDate}
							onDateSelect={handleDateSelect}
							calendarDays={calendarDays}
						/>
					</Box>
				</Portal>
			</Show>
		</Box>
	);
};

export default FaceliftDatePicker;
export type { DateRange };
